import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { Nomenclature, NomenclaturesService } from '../../../services/nomenclatures.service';
import { ComposantsService } from '../../../services/composants.service';

@Component({
  selector: 'app-voir-nomenclatures',
  imports: [CommonModule, FormsModule],
  templateUrl: './voir-nomenclatures.component.html',
  styleUrl: './voir-nomenclatures.component.scss'
})
export class VoirNomenclaturesComponent implements OnInit {
  protected nomenclatures: Nomenclature[] = [];
  protected nomenclaturePrixHt: Record<number, number> = {};
  protected filter = '';
  protected page = 1;
  protected readonly pageSize = 10;
  protected loading = false;
  protected error = '';
  protected success = '';
  protected deletingId: number | null = null;

  constructor(
    private readonly nomenclaturesService: NomenclaturesService,
    private readonly composantsService: ComposantsService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadNomenclatures();
  }

  protected get filteredNomenclatures(): Nomenclature[] {
    const q = this.normalizeText(this.filter);

    if (!q) {
      return this.nomenclatures;
    }

    return this.nomenclatures.filter((n) => {
      const text = [String(n.numero_nomenclature), n.nom, n.date_creation]
        .filter((v): v is string => !!v)
        .join(' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      return text.includes(q);
    });
  }

  protected get paginatedNomenclatures(): Nomenclature[] {
    const filtered = this.filteredNomenclatures;
    const start = (this.page - 1) * this.pageSize;
    return filtered.slice(start, start + this.pageSize);
  }

  protected get totalPages(): number {
    return Math.ceil(this.filteredNomenclatures.length / this.pageSize);
  }

  protected onFilterChange(): void {
    this.page = 1;
  }

  protected previousPage(): void {
    if (this.page > 1) {
      this.page--;
    }
  }

  protected nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
    }
  }

  protected refresh(): void {
    this.loadNomenclatures();
  }

  protected editNomenclature(nomenclature: Nomenclature): void {
    this.router.navigate(['/intranet/gestion-stocks/nomenclatures/modifier'], {
      queryParams: { nomenclatureId: nomenclature.id }
    });
  }

  protected deleteNomenclature(nomenclature: Nomenclature): void {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer la nomenclature ${nomenclature.numero_nomenclature}?`)) {
      return;
    }

    this.deletingId = nomenclature.id;

    this.zone.run(() => {
      this.nomenclaturesService.delete(nomenclature.id).subscribe({
        next: () => {
          this.success = 'Nomenclature supprimée avec succès.';
          this.loadNomenclatures();
          this.deletingId = null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.deletingId = null;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private normalizeText(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private loadNomenclatures(): void {
    this.loading = true;
    this.error = '';
    this.success = '';
    this.nomenclaturePrixHt = {};

    this.zone.run(() => {
      this.nomenclaturesService.list().subscribe({
        next: (nomenclatures) => {
          this.nomenclatures = nomenclatures;
          this.loadNomenclaturePrixHt();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    return err.statusText || 'Erreur inconnue';
  }

  private loadNomenclaturePrixHt(): void {
    if (this.nomenclatures.length === 0) {
      this.nomenclaturePrixHt = {};
      return;
    }

    const pending = this.nomenclatures.map((nomenclature) =>
      this.nomenclaturesService.getComposants(nomenclature.id).pipe(
        switchMap((composants) => {
          if (composants.length === 0) {
            return of({ id: nomenclature.id, prix: 0 });
          }

          const composantDetails = composants.map((nc) =>
            this.composantsService.getById(nc.composant_id).pipe(
              map((composant) => ({
                prix: composant.prix ?? 0,
                standard_emballage: composant.standard_emballage ? parseFloat(composant.standard_emballage) : 1,
                quantite: nc.quantite
              })),
              catchError(() => of({ prix: 0, standard_emballage: 1, quantite: nc.quantite }))
            )
          );

          return forkJoin(composantDetails).pipe(
            map((details) => {
              const prixTotal = details.reduce((sum, detail) => {
                if (detail.standard_emballage <= 0) {
                  return sum;
                }
                const prixUnitaire = detail.prix / detail.standard_emballage;
                return sum + prixUnitaire * detail.quantite;
              }, 0);
              return { id: nomenclature.id, prix: prixTotal };
            })
          );
        }),
        catchError(() => of({ id: nomenclature.id, prix: 0 }))
      )
    );

    forkJoin(pending).subscribe((results) => {
      const prices: Record<number, number> = {};
      for (const result of results) {
        prices[result.id] = result.prix;
      }
      this.zone.run(() => {
        this.nomenclaturePrixHt = prices;
        this.cdr.markForCheck();
      });
    });
  }
}
